// Phase 6.AM — RouteChangeObserver
// ---------------------------------------------------------------------------
// Renders nothing. Sits inside <BrowserRouter> and emits a `route_changed`
// lifecycle event every time react-router's location changes. The Owner
// Console "Frontend Navigation / Error Trace" panel uses these rows to
// reconstruct the user's exact navigation path, e.g.:
//
//     home_search_submitted (typed=Micron, target=MU)
//   → route_changed         (from=/, to=/analyze/MU)
//   → route_entry           (ticker=MU)
//   → fetch_started         (ticker=MU)
//   → response_received     (ticker=MU, http=200)
//   → response_body_parsed  (...)
//   → render_failed         (component=...)
//
// Without this we cannot prove "the URL actually changed to /analyze/MU"
// — we can only infer it from later Analyze.js events. If those events
// never fire, the operator needs to know whether navigation completed.
//
// Implementation notes:
//   * Uses useEffect([location]) so it fires AFTER the route transition
//     commits.
//   * Mints a fresh client_trace_id per route change so the Owner Console
//     can correlate route_changed → route_entry → fetch_started for the
//     SAME navigation, even when they're separated by re-mounts.
//   * Skips firing on the very first render (since there's no "from"
//     pathname — that case is covered by the route_entry on the target
//     page itself).

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { emitLifecycle, newClientTraceId } from "@/lib/lifecycle";
import { trackSpaRouteChange } from "@/lib/analytics";

export default function RouteChangeObserver() {
  const location = useLocation();
  const prevPathRef = useRef(null);
  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    const fromPath = prevPathRef.current;
    const toPath = location.pathname || "/";

    // Skip the very first render — we have nothing to compare against.
    // (route_entry on the landing page covers that case.)
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      prevPathRef.current = toPath;
      // Still emit an initial "page_loaded" event so the Owner Console
      // can see the user entered the SPA at all, in case nothing else
      // ever fires.
      try {
        emitLifecycle("page_loaded", {
          client_trace_id: newClientTraceId(),
          extra: {
            initial_path: toPath,
            search: location.search || null,
            hash: location.hash || null,
            referrer: (typeof document !== "undefined") ? document.referrer || null : null,
          },
        });
      } catch { /* swallow */ }
      return;
    }
    if (fromPath === toPath) return;

    // Slice 1 (2026-02-14) — Route-level scroll-to-top.
    // -----------------------------------------------------------------
    // Footer links (legal, privacy, transparency, etc.) inherited the
    // previous page's scroll offset because react-router does not
    // reset scroll on its own.  A single central rule here fixes it
    // for every route without touching individual link handlers.
    //
    // Guardrails:
    //   * A hash target (`#section-x`) is intentional in-page
    //     navigation and MUST be honored.
    //   * A `?keepScroll=1` opt-out lets any future flow (e.g. a
    //     pagination widget that swaps `?page=2`) preserve scroll.
    //   * Only fires on genuine pathname changes (from !== to) so
    //     hash-only or query-only updates never jump the reader.
    //   * `behavior: 'auto'` — no smooth-scroll ping-pong.
    try {
      const hasHash = !!(location.hash && location.hash.length > 1);
      const keepScroll = new URLSearchParams(location.search || "").get("keepScroll") === "1";
      if (!hasHash && !keepScroll && typeof window !== "undefined") {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }
    } catch { /* swallow — scroll is best-effort */ }

    // Extract ticker from /analyze/:ticker paths so the row keys neatly
    // into the existing ticker provenance trace.
    const toTickerMatch = toPath.match(/^\/analyze\/([A-Za-z0-9.\-]+)/);
    const toTicker = toTickerMatch ? toTickerMatch[1].toUpperCase() : null;
    const fromTickerMatch = (fromPath || "").match(/^\/analyze\/([A-Za-z0-9.\-]+)/);
    const fromTicker = fromTickerMatch ? fromTickerMatch[1].toUpperCase() : null;

    try {
      emitLifecycle("route_changed", {
        ticker: toTicker,
        client_trace_id: newClientTraceId(),
        extra: {
          from_path: fromPath,
          to_path: toPath,
          from_ticker: fromTicker,
          to_ticker: toTicker,
          search: location.search || null,
          hash: location.hash || null,
        },
      });
    } catch { /* swallow */ }
    prevPathRef.current = toPath;
    // Native Web Analytics — emit a privacy-safe manual $pageleave
    // for the previous canonical page and a $pageview for the new
    // one.  Does nothing before Analytics consent + SDK init +
    // anonymous distinct_id are all ready.  Fires ONLY on canonical
    // pathname change (hash-only / query-only changes are ignored
    // by the dependency filter below).
    try {
      trackSpaRouteChange(toPath);
    } catch { /* swallow */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return null;
}
